package org.zq.astrock_ai.quant

import kotlin.math.abs
import kotlin.math.max

/**
 * 公告/资讯情绪编码（FinBERT 同类输出：极性 / 置信度 / 事件类）。
 *
 * 跑在 commonMain，盘中细算可同步调用；不加载神经网络。
 * 比标题 `contains` 多三件事：最长短语优先、否定翻转、异常波动等套话降权。
 * 只给漏斗加减分；高置信停牌/立案/减持/预减才硬否决。
 */
enum class NewsPolarity { POSITIVE, NEGATIVE, NEUTRAL }

enum class NewsEventKind {
    HALT,
    REGULATORY,
    INQUIRY,
    DILUTION,
    EARNINGS_MISS,
    LAWSUIT,
    PLEDGE,
    EARNINGS_BEAT,
    BUYBACK,
    WIN_BID,
    INSIDER_BUY,
    /** 重大资产重组 / 并购 / 借壳等事件催化 */
    REORG,
    /** 重组失败 / 终止 / 被否 */
    REORG_FAIL,
    POLICY,
    ROUTINE,
    OTHER,
}

data class NewsTitleEncoding(
    val title: String = "",
    /** -1 利空 … +1 利好 */
    val polarity: Double = 0.0,
    val confidence: Double = 0.0,
    val label: NewsPolarity = NewsPolarity.NEUTRAL,
    val kind: NewsEventKind = NewsEventKind.OTHER,
    val hardOvernight: Boolean = false,
    val reason: String = "",
)

data class NewsSentimentBundle(
    val encodings: List<NewsTitleEncoding> = emptyList(),
    val polarity: Double = 0.0,
    val confidence: Double = 0.0,
    val label: NewsPolarity = NewsPolarity.NEUTRAL,
    /** ZoneMarketAdjust：正=风险（卖区下移） */
    val riskScore: Double = 0.0,
    /** 并入背景综合分，约 -3.5..+2.0 */
    val funnelDelta: Double = 0.0,
    /** 漏斗 newsScore 字段，2..12 */
    val newsScore10: Double = 5.0,
    val hardRejects: List<String> = emptyList(),
    val checklist: String = "",
    val hints: List<String> = emptyList(),
)

fun encodeNewsTitle(rawTitle: String): NewsTitleEncoding {
    val title = stripNewsPrefix(rawTitle)
    if (title.isBlank()) return NewsTitleEncoding()
    if (isRoutineHeadline(title) && !hasSeverePhrase(title)) {
        return NewsTitleEncoding(
            title = title,
            polarity = 0.0,
            confidence = 0.35,
            label = NewsPolarity.NEUTRAL,
            kind = NewsEventKind.ROUTINE,
            reason = "套话/提示性公告",
        )
    }
    val hits = findPhraseHits(title)
    if (hits.isEmpty()) {
        return NewsTitleEncoding(
            title = title,
            confidence = 0.25,
            kind = NewsEventKind.OTHER,
            reason = "未见可编码事件",
        )
    }
    var polarity = 0.0
    var weightSum = 0.0
    var best = hits.first()
    hits.forEach { hit ->
        val signed = if (hit.negated) -hit.rule.polarity * 0.45 else hit.rule.polarity
        polarity += signed * hit.rule.weight
        weightSum += hit.rule.weight
        if (!hit.negated && abs(hit.rule.polarity) >= abs(best.rule.polarity)) best = hit
    }
    polarity = (polarity / max(weightSum, 1.0)).coerceIn(-1.0, 1.0)
    val confidence = abs(polarity).coerceIn(0.2, 0.95)
    val kind = if (best.negated) NewsEventKind.OTHER else best.rule.kind
    val hard = hits.any { it.rule.hardOvernight && !it.negated }
    val label = when {
        polarity >= 0.18 -> NewsPolarity.POSITIVE
        polarity <= -0.18 -> NewsPolarity.NEGATIVE
        else -> NewsPolarity.NEUTRAL
    }
    val reason = buildString {
        append(if (best.negated) "否定后" else "")
        append(best.rule.phrase)
        if (hits.size > 1) append("等${hits.size}项")
    }
    return NewsTitleEncoding(
        title = title,
        polarity = polarity,
        confidence = confidence,
        label = label,
        kind = kind,
        hardOvernight = hard,
        reason = reason.take(28),
    )
}

fun encodeNewsBundle(
    titles: List<String>,
    freshness: List<Double> = emptyList(),
): NewsSentimentBundle {
    val pairs = titles.mapIndexed { i, raw ->
        stripNewsPrefix(raw) to (freshness.getOrNull(i) ?: 1.0)
    }.filter { it.first.isNotBlank() }.distinctBy { it.first }.take(16)
    if (pairs.isEmpty()) {
        return NewsSentimentBundle(
            checklist = "公告：暂无标题样本，收盘后请人工核对减持/立案/预减",
            hints = listOf("收盘后务必人工扫一眼公告再买隔夜"),
            newsScore10 = 5.0,
        )
    }
    val encodings = pairs.map { encodeNewsTitle(it.first) }
    val weights = encodings.mapIndexed { i, enc ->
        val fresh = pairs[i].second.coerceIn(0.4, 1.5)
        enc.polarity * enc.confidence * fresh
    }
    val worst = encodings.minBy { it.polarity }
    val hard = encodings.filter { it.hardOvernight }
    val polarity = if (hard.isNotEmpty()) {
        hard.minOf { it.polarity }
    } else {
        val minW = weights.min()
        val meanW = weights.average()
        (0.72 * minW + 0.28 * meanW).coerceIn(-1.0, 1.0)
    }
    val confidence = if (hard.isNotEmpty()) {
        hard.maxOf { it.confidence }
    } else {
        encodings.maxOf { it.confidence }
    }
    val label = when {
        polarity >= 0.18 -> NewsPolarity.POSITIVE
        polarity <= -0.18 -> NewsPolarity.NEGATIVE
        else -> NewsPolarity.NEUTRAL
    }
    val hasReorg = encodings.any { it.kind == NewsEventKind.REORG }
    val hasReorgFail = encodings.any { it.kind == NewsEventKind.REORG_FAIL }
    val hardRejects = hard.map { enc ->
        when (enc.kind) {
            NewsEventKind.HALT -> "存在停牌/异动提示，不做隔夜"
            NewsEventKind.REGULATORY -> "立案/处罚类公告高置信利空，隔夜易低开"
            NewsEventKind.EARNINGS_MISS -> "预减/预亏类公告高置信利空，隔夜易低开"
            NewsEventKind.DILUTION -> "减持类公告高置信利空，隔夜易低开"
            NewsEventKind.LAWSUIT -> "重大诉讼风险标题，不做隔夜"
            NewsEventKind.REORG_FAIL -> "重组失败/终止类公告，隔夜易低开"
            else -> "高置信利空公告，隔夜易低开"
        }
    }.distinct()
    val lead = (hard.firstOrNull() ?: encodings.firstOrNull { it.kind == NewsEventKind.REORG }
        ?: encodings.firstOrNull { it.kind == NewsEventKind.REORG_FAIL }
        ?: worst)
    val checklist = when {
        hard.isNotEmpty() ->
            "公告情绪：偏空（${lead.reason}）${lead.title.take(24)}"
        hasReorgFail ->
            "重组信号：失败/终止（${lead.reason}）${lead.title.take(24)}"
        hasReorg ->
            "重组信号：并购/资产重组（${lead.reason}）${lead.title.take(24)}（事件驱动，注意停牌与兑现）"
        label == NewsPolarity.POSITIVE ->
            "公告情绪：偏多（${lead.reason}）${lead.title.take(24)}（或高开，亦有兑现抛压）"
        label == NewsPolarity.NEGATIVE ->
            "公告情绪：偏空（${lead.reason}）${lead.title.take(24)}"
        else ->
            "公告情绪：中性（未见高置信减持/立案/预减/重组编码）"
    }
    val hints = buildList {
        if (label == NewsPolarity.POSITIVE) {
            add("利好也可能高开兑现；弱开早走，偏强可拖到尾盘仍须当日清仓")
        }
        if (hasReorg) {
            add("重组/并购属事件驱动：核对手续进度与停牌风险，隔夜仓位更轻，中线可跟踪进程")
        }
        if (hasReorgFail) {
            add("重组失败/终止后常有情绪杀跌，弱开优先兑现，勿博弈反抽")
        }
        if (encodings.any { it.kind == NewsEventKind.DILUTION && !it.hardOvernight }) {
            add("解禁/减持窗口有供给压力，仓位更轻")
        }
        if (encodings.any { it.kind == NewsEventKind.INQUIRY || it.kind == NewsEventKind.POLICY }) {
            add("留意盘后监管/问询，突发则次日优先早盘兑现")
        }
        if (encodings.any { it.kind == NewsEventKind.PLEDGE || it.kind == NewsEventKind.LAWSUIT }) {
            add("质押/诉讼类标题，仓位更轻")
        }
    }
    val reorgDelta = when {
        hasReorgFail -> -1.2
        hasReorg -> 0.8
        else -> 0.0
    }
    val funnelDelta = when {
        hard.isNotEmpty() -> (-3.2 + polarity).coerceIn(-3.5, -1.5)
        else -> (polarity * 2.4 + reorgDelta).coerceIn(-2.5, 2.2)
    }
    val newsScore10 = (6.5 + polarity * 4.2 + reorgDelta * 1.2).coerceIn(2.0, 12.0)
    val riskScore = (-polarity * 0.92).coerceIn(-0.55, 1.0)
    return NewsSentimentBundle(
        encodings = encodings,
        polarity = polarity,
        confidence = confidence,
        label = label,
        riskScore = riskScore,
        funnelDelta = funnelDelta,
        newsScore10 = newsScore10,
        hardRejects = hardRejects,
        checklist = checklist,
        hints = hints.distinct(),
    )
}

fun newsImpactDirection(title: String): String = when (encodeNewsTitle(title).label) {
    NewsPolarity.POSITIVE -> "偏利好"
    NewsPolarity.NEGATIVE -> "偏利空"
    NewsPolarity.NEUTRAL -> "中性"
}

fun newsImpactStrength(title: String): Int {
    val enc = encodeNewsTitle(title)
    return (38.0 + abs(enc.polarity) * 50.0 + enc.confidence * 12.0).toInt().coerceIn(30, 90)
}

private data class PhraseRule(
    val phrase: String,
    val polarity: Double,
    val kind: NewsEventKind,
    val weight: Double = 1.0,
    val hardOvernight: Boolean = false,
)

private data class PhraseHit(
    val rule: PhraseRule,
    val start: Int,
    val end: Int,
    val negated: Boolean,
)

private val phraseRules: List<PhraseRule> = listOf(
    PhraseRule("暂停上市", -0.98, NewsEventKind.HALT, hardOvernight = true),
    PhraseRule("终止上市", -0.98, NewsEventKind.HALT, hardOvernight = true),
    PhraseRule("异动停牌", -0.95, NewsEventKind.HALT, hardOvernight = true),
    PhraseRule("停牌", -0.92, NewsEventKind.HALT, hardOvernight = true),
    PhraseRule("退市风险警示", -0.90, NewsEventKind.HALT, hardOvernight = true),
    PhraseRule("实施退市", -0.96, NewsEventKind.HALT, hardOvernight = true),
    PhraseRule("立案调查", -0.95, NewsEventKind.REGULATORY, hardOvernight = true),
    PhraseRule("立案告知", -0.92, NewsEventKind.REGULATORY, hardOvernight = true),
    PhraseRule("被立案", -0.92, NewsEventKind.REGULATORY, hardOvernight = true),
    PhraseRule("财务造假", -0.96, NewsEventKind.REGULATORY, hardOvernight = true),
    PhraseRule("虚假陈述", -0.90, NewsEventKind.REGULATORY, hardOvernight = true),
    PhraseRule("行政处罚", -0.78, NewsEventKind.REGULATORY, hardOvernight = true),
    PhraseRule("监管措施", -0.70, NewsEventKind.REGULATORY, hardOvernight = true),
    PhraseRule("预减", -0.82, NewsEventKind.EARNINGS_MISS, hardOvernight = true),
    PhraseRule("预亏", -0.85, NewsEventKind.EARNINGS_MISS, hardOvernight = true),
    PhraseRule("业绩大幅下滑", -0.72, NewsEventKind.EARNINGS_MISS, hardOvernight = true),
    PhraseRule("由盈转亏", -0.80, NewsEventKind.EARNINGS_MISS, hardOvernight = true),
    PhraseRule("股份减持", -0.78, NewsEventKind.DILUTION, hardOvernight = true),
    PhraseRule("减持计划", -0.80, NewsEventKind.DILUTION, hardOvernight = true),
    PhraseRule("减持预披露", -0.78, NewsEventKind.DILUTION, hardOvernight = true),
    PhraseRule("拟合减持", -0.76, NewsEventKind.DILUTION, hardOvernight = true),
    PhraseRule("减持股份", -0.76, NewsEventKind.DILUTION, hardOvernight = true),
    PhraseRule("拟减持", -0.74, NewsEventKind.DILUTION, hardOvernight = true),
    PhraseRule("股东减持", -0.74, NewsEventKind.DILUTION, hardOvernight = true),
    PhraseRule("质押平仓", -0.88, NewsEventKind.PLEDGE, hardOvernight = true),
    PhraseRule("债务逾期", -0.86, NewsEventKind.LAWSUIT, hardOvernight = true),
    PhraseRule("被执行", -0.70, NewsEventKind.LAWSUIT, hardOvernight = true),
    PhraseRule("重大资产重组失败", -0.92, NewsEventKind.REORG_FAIL, hardOvernight = true),
    PhraseRule("终止重大资产重组", -0.88, NewsEventKind.REORG_FAIL, hardOvernight = true),
    PhraseRule("重组预案被否", -0.86, NewsEventKind.REORG_FAIL, hardOvernight = true),
    PhraseRule("重组失败", -0.88, NewsEventKind.REORG_FAIL, hardOvernight = true),
    PhraseRule("终止重组", -0.84, NewsEventKind.REORG_FAIL, hardOvernight = true),
    PhraseRule("重组终止", -0.84, NewsEventKind.REORG_FAIL, hardOvernight = true),
    PhraseRule("重大资产重组停牌", -0.95, NewsEventKind.HALT, hardOvernight = true),
    PhraseRule("筹划重大资产重组", 0.48, NewsEventKind.REORG),
    PhraseRule("重大资产重组", 0.52, NewsEventKind.REORG),
    PhraseRule("发行股份购买资产", 0.50, NewsEventKind.REORG),
    PhraseRule("购买资产并募集配套资金", 0.48, NewsEventKind.REORG),
    PhraseRule("并购重组", 0.46, NewsEventKind.REORG),
    PhraseRule("吸收合并", 0.44, NewsEventKind.REORG),
    PhraseRule("借壳上市", 0.42, NewsEventKind.REORG),
    PhraseRule("重组过会", 0.64, NewsEventKind.REORG),
    PhraseRule("重组获批", 0.66, NewsEventKind.REORG),
    PhraseRule("重组成功", 0.58, NewsEventKind.REORG),
    PhraseRule("资产注入", 0.42, NewsEventKind.REORG),
    PhraseRule("要约收购", 0.40, NewsEventKind.REORG),
    PhraseRule("收购报告书", 0.36, NewsEventKind.REORG),
    PhraseRule("问询函回复", -0.18, NewsEventKind.INQUIRY),
    PhraseRule("回复问询", -0.18, NewsEventKind.INQUIRY),
    PhraseRule("收到问询函", -0.48, NewsEventKind.INQUIRY),
    PhraseRule("问询函", -0.42, NewsEventKind.INQUIRY),
    PhraseRule("关注函", -0.40, NewsEventKind.INQUIRY),
    PhraseRule("监管工作函", -0.38, NewsEventKind.INQUIRY),
    PhraseRule("解禁", -0.32, NewsEventKind.DILUTION),
    PhraseRule("股权质押", -0.35, NewsEventKind.PLEDGE),
    PhraseRule("股份质押", -0.35, NewsEventKind.PLEDGE),
    PhraseRule("商誉减值", -0.45, NewsEventKind.EARNINGS_MISS),
    PhraseRule("净利下滑", -0.40, NewsEventKind.EARNINGS_MISS),
    PhraseRule("业绩下滑", -0.38, NewsEventKind.EARNINGS_MISS),
    PhraseRule("诉讼", -0.36, NewsEventKind.LAWSUIT),
    PhraseRule("仲裁", -0.34, NewsEventKind.LAWSUIT),
    PhraseRule("判决", -0.30, NewsEventKind.LAWSUIT),
    PhraseRule("不减持", 0.35, NewsEventKind.INSIDER_BUY),
    PhraseRule("终止减持", 0.32, NewsEventKind.INSIDER_BUY),
    PhraseRule("股份回购", 0.62, NewsEventKind.BUYBACK),
    PhraseRule("回购股份", 0.62, NewsEventKind.BUYBACK),
    PhraseRule("回购注销", 0.58, NewsEventKind.BUYBACK),
    PhraseRule("控股股东增持", 0.70, NewsEventKind.INSIDER_BUY),
    PhraseRule("增持", 0.55, NewsEventKind.INSIDER_BUY),
    PhraseRule("预增", 0.68, NewsEventKind.EARNINGS_BEAT),
    PhraseRule("业绩预盈", 0.60, NewsEventKind.EARNINGS_BEAT),
    PhraseRule("扭亏", 0.58, NewsEventKind.EARNINGS_BEAT),
    PhraseRule("中标候选人", 0.48, NewsEventKind.WIN_BID),
    PhraseRule("中标", 0.58, NewsEventKind.WIN_BID),
    PhraseRule("中选", 0.52, NewsEventKind.WIN_BID),
    PhraseRule("签署合同", 0.45, NewsEventKind.WIN_BID),
    PhraseRule("重大合同", 0.50, NewsEventKind.WIN_BID),
    PhraseRule("获得批复", 0.42, NewsEventKind.WIN_BID),
    PhraseRule("获批", 0.40, NewsEventKind.WIN_BID),
    PhraseRule("股权激励", 0.28, NewsEventKind.INSIDER_BUY),
    PhraseRule("现金分红", 0.30, NewsEventKind.EARNINGS_BEAT),
    PhraseRule("窗口指导", -0.28, NewsEventKind.POLICY),
    PhraseRule("证监会", -0.12, NewsEventKind.POLICY, weight = 0.4),
).sortedByDescending { it.phrase.length }

/** 资讯事件标签，供荐股卡片展示 */
fun newsEventTagsOf(bundle: NewsSentimentBundle): List<String> {
    val tags = linkedSetOf<String>()
    bundle.encodings.forEach { enc ->
        when (enc.kind) {
            NewsEventKind.REORG -> tags += "重组催化"
            NewsEventKind.REORG_FAIL -> tags += "重组失败"
            NewsEventKind.HALT -> tags += "停牌风险"
            NewsEventKind.REGULATORY -> tags += "监管利空"
            NewsEventKind.DILUTION -> tags += "减持压力"
            NewsEventKind.EARNINGS_MISS -> tags += "业绩利空"
            NewsEventKind.EARNINGS_BEAT -> tags += "业绩利好"
            NewsEventKind.BUYBACK -> tags += "回购"
            NewsEventKind.WIN_BID -> tags += "中标/合同"
            NewsEventKind.INSIDER_BUY -> tags += "增持"
            NewsEventKind.INQUIRY -> tags += "问询"
            NewsEventKind.LAWSUIT -> tags += "诉讼"
            NewsEventKind.PLEDGE -> tags += "质押"
            NewsEventKind.POLICY -> tags += "政策"
            else -> Unit
        }
    }
    return tags.take(6)
}

fun reorgLabelOf(bundle: NewsSentimentBundle): String {
    val fail = bundle.encodings.firstOrNull { it.kind == NewsEventKind.REORG_FAIL }
    if (fail != null) return "重组失败/终止：${fail.title.take(28)}"
    val hit = bundle.encodings.firstOrNull { it.kind == NewsEventKind.REORG } ?: return ""
    return "重组/并购：${hit.title.take(28)}"
}

/**
 * 重组事件对总分的额外加权：中线跟进程，隔夜更谨慎；失败一律扣分。
 */
fun reorgScoreBoost(bundle: NewsSentimentBundle, swing: Boolean): Double {
    val fail = bundle.encodings.any { it.kind == NewsEventKind.REORG_FAIL }
    val hit = bundle.encodings.any { it.kind == NewsEventKind.REORG }
    return when {
        fail -> -4.0
        hit && swing -> 3.5
        hit -> 1.2
        else -> 0.0
    }
}

private val negationMarks = listOf("不", "未", "非", "无", "否认", "终止", "取消", "不会", "暂不", "没有", "尚未")

private fun stripNewsPrefix(raw: String): String = raw
    .replace("[东财公告] ", "")
    .replace("[东财资讯] ", "")
    .replace("[新浪] ", "")
    .trim()

private fun isRoutineHeadline(title: String): Boolean =
    title.contains("股票交易异常波动") ||
        title.contains("日常关联交易") ||
        title.contains("召开股东大会") ||
        title.contains("股东大会通知") ||
        title.contains("独立董事") ||
        title.contains("延期回复") ||
        (title.contains("提示性公告") && !hasSeverePhrase(title))

private fun hasSeverePhrase(title: String): Boolean =
    phraseRules.any { it.hardOvernight && title.contains(it.phrase) }

private fun findPhraseHits(title: String): List<PhraseHit> {
    val hits = mutableListOf<PhraseHit>()
    phraseRules.forEach { rule ->
        var from = 0
        while (from < title.length) {
            val i = title.indexOf(rule.phrase, from)
            if (i < 0) break
            val end = i + rule.phrase.length
            val covered = hits.any { i >= it.start && end <= it.end }
            if (!covered) {
                hits += PhraseHit(rule, i, end, negated = isNegated(title, i))
            }
            from = i + 1
        }
    }
    return hits
}

private fun isNegated(title: String, start: Int): Boolean {
    val from = (start - 4).coerceAtLeast(0)
    val window = title.substring(from, start)
    return negationMarks.any { window.contains(it) }
}
